/**
 * Image Preloader Utility
 * Intelligently preloads onboarding images for optimal performance
 */

import { ONBOARDING_PAGES, LANDING_PAGE_DATA } from './onboardingData';

// Critical pages that should always be preloaded immediately
const CRITICAL_PAGE_IDS = [1, 6]; // Page 1 (Welcome) and Page 6 (Get Started)

// Track which pages have been preloaded
const preloadedPages = new Set<number>();

/**
 * Preload images for a specific page
 * Uses Image constructor for efficient preloading
 */
export function preloadPageImages(pageId: number): Promise<void> {
  return new Promise((resolve) => {
    // Skip if already preloaded
    if (preloadedPages.has(pageId)) {
      resolve();
      return;
    }

    let page;
    if (pageId === 6) {
      page = LANDING_PAGE_DATA;
    } else {
      page = ONBOARDING_PAGES.find((p) => p.id === pageId);
    }

    if (!page) {
      resolve();
      return;
    }

    const images = [
      ...page.images.mobile,
      ...page.images.desktop
    ];

    // Generate @2x variants for high DPI screens
    const allImages = images.flatMap((img) => [
      img, // @1x
      img.replace('@1x', '@2x') // @2x
    ]);

    let loadedCount = 0;
    const totalImages = allImages.length;

    // Early resolve if no images
    if (totalImages === 0) {
      preloadedPages.add(pageId);
      resolve();
      return;
    }

    allImages.forEach((imageSrc) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          preloadedPages.add(pageId);
          resolve();
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          preloadedPages.add(pageId);
          resolve();
        }
      };
      img.src = imageSrc;
    });
  });
}

/**
 * Preload critical pages immediately (Page 1 & 6)
 */
export function preloadCriticalPages(): void {
  CRITICAL_PAGE_IDS.forEach((pageId) => {
    preloadPageImages(pageId).catch(() => {
      // Silently fail - preloading is an optimization, not critical
    });
  });
}

/**
 * Preload next page with ~100ms delay
 * This allows the current page to render first, then preload next
 */
export function preloadNextPage(currentPageId: number, delayMs: number = 100): void {
  const nextPageId = currentPageId + 1;
  
  // Don't preload beyond page 6
  if (nextPageId > 6) {
    return;
  }

  setTimeout(() => {
    preloadPageImages(nextPageId).catch(() => {
      // Silently fail - preloading is an optimization, not critical
    });
  }, delayMs);
}

/**
 * Check if a page's images are preloaded
 */
export function isPagePreloaded(pageId: number): boolean {
  return preloadedPages.has(pageId);
}

/**
 * Reset preload cache (useful for testing)
 */
export function resetPreloadCache(): void {
  preloadedPages.clear();
}

